import { useEffect, useRef, useState, type CSSProperties } from 'react'

import { Link as RouterLink } from 'react-router-dom'

import {
  Box,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  alpha,
  keyframes,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import type { Theme } from '@mui/material'
import { CheckIcon, ChevronDownIcon, ChevronRightIcon, GiftIcon } from 'lucide-react'

import { Button, Card } from '@repo/ui'

import { useSnackbar } from 'contexts'
import { useCanAccess, type ChecklistBlock, type ChecklistItem } from 'hooks'
import { useGrantSetupCompletionReward } from 'hooks/api/useSetupCompletionReward'

// Direct subpath import, not the `components` barrel: that barrel re-exports the onboarding
// folder, which reaches back into components/input/Search — a cycle.
import { useProductTour } from 'components/onboarding'
import { ProgressPill } from 'components/display/ProgressPill/ProgressPill'
import { Progress } from 'components/display/Progress/Progress'

/**
 * The width of every marker in the card's left-hand column — a row's tick, a block's numeral,
 * and the reward's gift.
 *
 * ONE constant because the three form a vertical column, and three hardcoded sizes (24 / 20 /
 * 18) read as ragged alignment rather than as hierarchy. Anything that sits in that column
 * uses this; the GLYPH inside it can differ, the FOOTPRINT cannot.
 */
const MARKER = 24

/**
 * Local copies of the app's page-slide timing (`ASK_PANEL_SLIDE_DURATION_MS` /
 * `ASK_PANEL_SLIDE_EASE` in `src/ask/useAskStore.ts`), NOT an import — reaching into `ask/`
 * from a Home component would be a bad dependency direction (Home has nothing to do with the
 * Ask feature; the two just happen to want the same motion language). Redeclared rather than
 * hoisted to a shared location: this is the only file outside `ask/` that needs this exact
 * curve today.
 *
 * Used for BOTH accordion collapses on this card (the card-level collapse and each block's own
 * expand/toggle) so the whole card reads as one motion vocabulary rather than two.
 */
const CHECKLIST_TRANSITION_DURATION_MS = 240
const CHECKLIST_TRANSITION_EASE = 'cubic-bezier(0.32, 0.72, 0, 1)'

/**
 * The `grid-template-rows: 0fr -> 1fr` accordion trick — a CSS-only height transition that
 * needs no measured pixel height and, critically, needs no JS/rAF to drive it.
 *
 * This is NOT a style preference: the Claude preview pane this app is verified in fires ZERO
 * `requestAnimationFrame` callbacks, so anything driven by rAF (a JS height-measuring
 * animation, `react-spring`, etc.) never starts there and would be unverifiable. A `transition`
 * on a CSS property is driven by the compositor, not by script, so it runs regardless.
 *
 * `grid-template-rows: 0fr` collapses the row to zero height without `display: none` (so it
 * stays laid out and transitionable); `1fr` opens it to exactly its content's height with no
 * measurement step. The inner wrapper needs `overflow: hidden` and `minHeight: 0` — a grid row
 * item defaults to `min-height: auto`, which would let it overflow its own 0fr row and skip
 * the collapsed state entirely.
 */
const collapsibleSx = (isOpen: boolean) => ({
  display: 'grid',
  gridTemplateRows: isOpen ? '1fr' : '0fr',
  transition: `grid-template-rows ${CHECKLIST_TRANSITION_DURATION_MS}ms ${CHECKLIST_TRANSITION_EASE}`,
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
})

const chevronRotateSx = (isOpen: boolean) => ({
  transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
  transition: `transform ${CHECKLIST_TRANSITION_DURATION_MS}ms ${CHECKLIST_TRANSITION_EASE}`,
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
})

/**
 * The outline on an INCOMPLETE marker.
 *
 * `primary[100]` measured **1.28:1** against the card in dark theme (rgb(39,39,42) on
 * rgb(15,15,18)) — WCAG asks 3:1 for a UI component outline, so this was roughly a
 * quarter of the floor and read as a smudge rather than an empty state. Measured in the
 * browser, not inferred.
 *
 * `text.secondary` at full strength measures 7.47:1, which is too loud for a marker
 * whose job is "not done yet". Blending it at 0.6 lands around 3.3 — clearly visible,
 * comfortably over the floor, still quieter than the label beside it.
 *
 * ALPHA rather than a literal, deliberately. Alpha composites against whatever card sits
 * behind it, so this adapts to both themes by construction. Three separate contrast bugs
 * shipped in this app on 2026-08-27 from a literal that was correct in one theme and
 * wrong in the other (`primary[950]` remaps to #ffffff in dark; `common.white` on a light
 * panel; `white[100]` on a state with no fill). A value that cannot be theme-wrong is
 * worth more here than a value hand-picked per theme.
 *
 * NOTE this is for MARKERS only. The card outline further down stays `primary[100]` — a
 * container edge is meant to be barely there; a state indicator is not.
 */
const INCOMPLETE_MARKER_BORDER = (theme: Theme) => alpha(theme.palette.text.secondary, 0.6)

/**
 * A COMPLETED marker is muted, not solid brand.
 *
 * It used to be a solid `brand[500]` disc with a white tick, argued as "progress through
 * the product, so it reads in the product's own colour". The argument is fine and the
 * result inverted the page's hierarchy: a completed step is the LOWEST-information
 * element on screen -- it is done, it needs nothing -- yet it was rendered in the loudest,
 * most saturated colour the system has, while the one step still OUTSTANDING was a plain
 * empty circle. Eleven finished items shouted and the single unfinished item whispered,
 * which is backwards for a checklist, whose whole job is pointing at what remains.
 *
 * It also spent a scarce signal. Brand purple already means "you are here" in the sidebar,
 * a few hundred pixels away, and the codebase states the principle elsewhere (the Ask
 * composer's send button: "purple is reserved for states that mean something"). Using the
 * same hue for "current location" and "this is finished", adjacent on screen, devalues
 * both -- which is what "the bright checkmarks compete with the sidebar" was.
 *
 * A low-alpha brand tint keeps the family relationship (it is still recognisably the same
 * colour, so completion still reads as progress rather than as a disabled state) while
 * dropping the volume enough that the eye lands on the incomplete row instead. Alpha
 * rather than a literal so it composites correctly against both themes' card surfaces --
 * three contrast bugs shipped in one day from theme-wrong literals.
 */
const DONE_MARKER_FILL = (theme: Theme) => alpha(theme.palette.brand[500], 0.16)

/** The tick itself carries the brand colour at full strength. On the tinted disc above it
 *  has real contrast, and it keeps the mark legible at 14px where a washed-out glyph would
 *  not be -- muting the FILL is the goal, not making the tick hard to see. */
const DONE_MARKER_TICK = (theme: Theme) => theme.palette.brand[600]


/**
 * One checklist row.
 *
 * A row the viewer cannot action is DISABLED with a tooltip naming the permission (§27), not
 * hidden — the block as a whole is already gated on `manage_company`, so this only fires in
 * the custom-role case where a company granted `manage_company` without, say,
 * `billing_change_plan`. Hiding there would make the checklist silently shorter for some
 * people at the same brand, and "3 of 5" vs "3 of 6" is a difference nobody can explain.
 */
const ChecklistRow = ({ item }: { item: ChecklistItem }) => {
  const theme = useTheme()
  const { isHasAccess } = useCanAccess({ action: item.permission })
  const { hasSeenTour, isTourProgressReady } = useProductTour()

  /*
    ONE target per row: clicking it goes to the page AND starts the tour there.

    This replaced a separate "Show me how" button, and the button was subtly broken as well as
    cluttered. Tour steps are anchored to DOM elements on the DESTINATION page
    (`isTourOnPage` resolves them by anchor), so firing `startTour` from Home — where none of
    those elements exist — had nothing to attach to. The tour has to begin after the
    navigation, not before it.

    `?tour=<id>` is the provider's own deep-link mechanism, and its effect deliberately runs on
    every param change rather than once on mount precisely so an in-app navigation can carry
    it. It strips the param immediately, so the URL does not stay contaminated and a refresh
    does not replay the tour.

    Appended only for a tour this user has NOT already seen: the row stays a useful shortcut to
    the page forever, and re-teaching someone who finished the tour months ago would make the
    row feel like it had stopped working. `undefined` from `hasSeenTour` means NOT YET KNOWN,
    which is why the ready flag is checked rather than treating unknown as unseen — that is the
    distinction its docblock exists to protect.
  */
  /*
    ALWAYS launches the tour, including one this user has seen before.

    It used to append `?tour=` only for an UNSEEN tour, on the reasoning that re-teaching
    someone who finished it months ago makes the row feel broken. That reasoning holds for
    the tours DIALOG and does not hold here: clicking a checklist step is an explicit request
    for help ON THAT STEP, and treating a months-old dismissal as a permanent opt-out silently
    removes the feature from the exact person still working through setup.

    A step is also not finished by visiting its page — several are settings a tour walks you
    into actually choosing. `hasSeenTour` is still read so the row can say so.
  */
  const hasTour = Boolean(item.tourId)
  const to = hasTour ? `${item.route}?tour=${item.tourId}` : item.route

  const tick = (
    <Box
      sx={{
        width: MARKER,
        height: MARKER,
        flexShrink: 0,
        borderRadius: '50%',
        display: 'grid',
        placeItems: 'center',
        // Brand purple, not success green. A completed setup step is not a "success" state in
        // the alert sense — nothing went right or wrong — it is progress through the product,
        // so it reads in the product's own colour, the one the sidebar uses for the selected row.
        bgcolor: item.done ? DONE_MARKER_FILL(theme) : 'transparent',
        border: item.done ? 'none' : `1px solid ${INCOMPLETE_MARKER_BORDER(theme)}`,
      }}
    >
      {item.done && <CheckIcon size={14} strokeWidth={2} color={DONE_MARKER_TICK(theme)} />}
    </Box>
  )

  const label = (
    <Typography
      variant="body2"
      sx={{
        textDecoration: item.done ? 'line-through' : 'none',
        color: item.done ? 'text.secondary' : 'text.primary',
      }}
    >
      {item.label}
    </Typography>
  )

  /*
    The row is a Stack of SIBLINGS, not one big link, because it now carries two different
    actions: "go do it" (the label → the completing surface) and "show me how" (the tour).
    Nesting a button inside an anchor is invalid HTML and gives keyboard and screen-reader
    users one target where there are two, so the link wraps the label only.
  */
  const row = (
    <Stack
      direction="row"
      alignItems="center"
      spacing={3}
      sx={{
        /*
          THE HOVER FILL IS A CHIP AROUND THE ROW, NOT A BAND BEHIND IT.

          Was `py: 2, px: 2, mx: -2` — 8px of inset on every side. Measured hovering a row:
          the fill's left edge sat exactly 8px from the 24px marker's left edge, which is
          LESS than the 12px gap between the marker and the label beside it. The fill was
          therefore tighter to the marker than the row's own internal spacing, so it read as
          a band jammed against the tick rather than as a surface containing it, and at 40px
          tall the 8px corners barely registered as rounded.

          `px: 3` (12px) makes the fill's left inset MATCH the marker→label gap, so the chip
          has one consistent rhythm across its whole width. `py: 2.5` (10px) keeps it a touch
          tighter vertically than horizontally — a list row wants to stay a row, and going
          symmetric at 12px pushed each row to 48px, which spaces a twelve-item list out
          further than the card can afford.

          ⚠️ `mx` MUST stay the exact negative of `px`. That pairing is the only reason the
          fill can bleed past the content column without moving the content — break it and
          every row shifts horizontally relative to the block title above it.

          `borderRadius: 2` (8px) is kept deliberately rather than stepped up: it is what the
          block sections and the reward banner on this same card use, and what the platform's
          other hover-chip rows use (`PermissionsList`, `RolePermissionsInfo`). At the new
          44px height it reads clearly rounded; a larger value here would be a one-off.
        */
        py: 2.5,
        px: 3,
        /*
          NO negative `mx`. This was `mx: -3`, which bled the hover fill 12px OUTWARD past
          the content column so the row's text stayed aligned with everything else on the
          card while the chip ran wider than it.

          Measured, that puts the fill at 630→1324 inside a card whose border is at
          617/1337 with 24px of padding — so the chip cleared the card edge by 12px while
          the card's own gutter is 24px. Against a 24px gutter a 12px clearance does not
          read as an inset chip; it reads as a band that nearly reaches the edge, which is
          the report ("still needs border radius and left/right padding like a list item"
          — the 8px radius was present the whole time and simply could not be seen at that
          clearance).

          Dropping the bleed puts the fill exactly on the content column, so the card's
          own 24px gutter becomes the chip's side margin and the rounding has room to
          register. The cost is that the row's marker now sits 12px inside the column
          rather than flush with it — which is what a list item does, and is why the
          negative margin was worth losing rather than tuning.
        */
        opacity: !isHasAccess && !item.done ? 0.5 : 1,
        color: 'text.primary',
        borderRadius: 2,
        transition: 'background-color 120ms',
        /*
          NOT `action.hover`, which measured 1.21:1 against this card in dark mode
          (rgba(255,255,255,0.08) compositing to rgb(34,34,37) over the card's own
          rgb(15,15,18)) and fainter still in light — below the point where a fill
          registers as a state change at all, so the row effectively had no hover
          however much padding it was given.

          `text.primary` at 0.15 measures ~1.50:1 dark and ~1.42:1 light: the usual
          quiet-but-present band. Derived from the text colour rather than a literal
          so it inverts with the theme instead of needing a per-mode value.
        */
        ...(isHasAccess &&
          !item.done && {
            '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.15) },
          }),
      }}
    >
      {tick}

      {item.done || !isHasAccess ? (
        <Box sx={{ flexGrow: 1 }}>{label}</Box>
      ) : item.onSelect ? (
        // VRG-655 — an action row ("Refer & earn" opens its modal in place). A real
        // <button>, so it is keyboard-reachable and never navigates.
        <Box
          component="button"
          type="button"
          onClick={item.onSelect}
          sx={{
            flexGrow: 1,
            textAlign: 'left',
            p: 0,
            border: 0,
            background: 'none',
            font: 'inherit',
            color: 'inherit',
            cursor: 'pointer',
          }}
        >
          {label}
        </Box>
      ) : (
        <Box
          component={RouterLink}
          to={to}
          sx={{ flexGrow: 1, textDecoration: 'none', color: 'inherit' }}
        >
          {label}
        </Box>
      )}

      {!item.done && isHasAccess && <ChevronRightIcon size={16} strokeWidth={1} />}
    </Stack>
  )

  if (!isHasAccess && !item.done) {
    return (
      <Tooltip title={`You need the "${item.permission}" permission. Ask an admin to grant it.`} placement="top-start">
        {/* A disabled control does not fire pointer events, so the tooltip needs a wrapper
            that does — otherwise the explanation is unreachable for the people it is for. */}
        <Box>{row}</Box>
      </Tooltip>
    )
  }

  return row
}

/**
 * Small counts read as words in UI copy — "these four", not "these 4".
 *
 * Still DERIVED from the block rather than written into the sentence: the copy said "these
 * five" for a while after the blocks went from five items to four, and a hardcoded number
 * sitting beside the number it describes is the one place that drift looks worst.
 */
/**
 * A single settle when a reward lands — a small rise and fade, not a bounce.
 *
 * The card has just changed colour entirely, which is already the loudest signal on the
 * page; the motion only has to say "this just happened" rather than compete with it.
 */
const rewardLand = keyframes`
  0%   { transform: translateY(4px); opacity: 0.6; }
  100% { transform: translateY(0);   opacity: 1; }
`

/**
 * One confetti piece's flight: out, up, then down and gone.
 *
 * CSS KEYFRAMES, NOT A LIBRARY AND NOT rAF, and both halves are load-bearing:
 *
 *  - No new dependency. A canvas confetti package is ~10-15kB for one burst that fires at
 *    most three times in a company's lifetime, on a page that is otherwise all layout.
 *  - No `requestAnimationFrame`. The Claude preview pane this app is verified in fires ZERO
 *    rAF callbacks, so a JS-driven burst would never start there and could not be reviewed —
 *    the same constraint `collapsibleSx` above documents for the accordion. A CSS animation
 *    is driven by the compositor, so it runs regardless of whether script gets frames.
 *
 * Per-piece variation comes from CSS custom properties set inline, so all fourteen pieces
 * share ONE keyframes rule rather than generating fourteen.
 */
const rewardBurst = keyframes`
  0%   { transform: translate(0, 0) rotate(0deg) scale(0.4); opacity: 0; }
  12%  { opacity: 1; }
  45%  {
    transform: translate(calc(var(--dx) * 0.6), var(--peak)) rotate(calc(var(--rot) * 0.5)) scale(1);
    opacity: 1;
  }
  100% {
    transform: translate(var(--dx), var(--dy)) rotate(var(--rot)) scale(0.9);
    opacity: 0;
  }
`

/**
 * The pieces, as a FIXED table rather than `Math.random()` at render.
 *
 * A random layout would reshuffle on every re-render — and this component mounts at the exact
 * moment a mutation settles and the query invalidates, so it re-renders immediately. Randoms
 * there would restart each piece's animation from a new position mid-flight, which reads as a
 * glitch rather than a burst. Fixed values also make the thing reviewable: the same burst
 * every time is one that can be looked at twice.
 *
 * `left` is a percentage of the banner, so the burst spans it rather than erupting from one
 * point — the banner is a wide, short band, and a single-origin spray in the middle of it
 * leaves the ends dead.
 */
const CONFETTI_PIECES: {
  left: number
  dx: number
  peak: number
  dy: number
  rot: number
  delay: number
  duration: number
  round: boolean
  tone: 0 | 1 | 2 | 3 | 4
}[] = [
  { left: 8, dx: -34, peak: -30, dy: 26, rot: -220, delay: 0, duration: 1100, round: false, tone: 0 },
  { left: 14, dx: -12, peak: -44, dy: 34, rot: 160, delay: 60, duration: 1180, round: true, tone: 2 },
  { left: 21, dx: 18, peak: -36, dy: 30, rot: -140, delay: 20, duration: 1040, round: false, tone: 1 },
  { left: 28, dx: -26, peak: -50, dy: 24, rot: 240, delay: 90, duration: 1220, round: false, tone: 3 },
  { left: 35, dx: 8, peak: -28, dy: 38, rot: -180, delay: 40, duration: 1080, round: true, tone: 0 },
  { left: 42, dx: 30, peak: -46, dy: 22, rot: 200, delay: 10, duration: 1160, round: false, tone: 2 },
  { left: 49, dx: -20, peak: -54, dy: 32, rot: -260, delay: 70, duration: 1240, round: false, tone: 4 },
  { left: 56, dx: 14, peak: -32, dy: 28, rot: 150, delay: 30, duration: 1060, round: true, tone: 1 },
  { left: 63, dx: -30, peak: -42, dy: 36, rot: -190, delay: 100, duration: 1200, round: false, tone: 0 },
  { left: 70, dx: 24, peak: -48, dy: 26, rot: 230, delay: 50, duration: 1140, round: false, tone: 3 },
  { left: 77, dx: -10, peak: -34, dy: 40, rot: -170, delay: 15, duration: 1020, round: true, tone: 2 },
  { left: 84, dx: 32, peak: -52, dy: 24, rot: 210, delay: 80, duration: 1220, round: false, tone: 1 },
  { left: 90, dx: -22, peak: -38, dy: 34, rot: -240, delay: 45, duration: 1120, round: false, tone: 4 },
  { left: 95, dx: 12, peak: -26, dy: 30, rot: 180, delay: 25, duration: 1060, round: true, tone: 0 },
]

/**
 * How long the whole burst needs before it can be unmounted. Longest duration (1240ms) plus
 * the longest delay (100ms), plus slack.
 *
 * NOT exported: this file's only export is a component, and adding a non-component export
 * would break Vite's Fast Refresh for the whole module. It has no consumer outside this file.
 */
const REWARD_CONFETTI_MS = 1400

/**
 * A one-shot burst over the reward banner, fired when a claim actually credits.
 *
 * MIXED LIGHTNESS ON PURPOSE. The banner changes underneath this mid-burst — a 12% brand tint
 * at the instant of the click, then the earned chrome treatment a beat later when the
 * invalidated query lands — and it does that on both a near-black and a near-white card. The
 * palette therefore spans the ramp rather than picking one "correct" tone: the light tones
 * carry the burst on a dark card, the dark tones carry it on a light one. Cheaper and far less
 * brittle than branching on mode AND on mutation state for a 1.1s animation.
 *
 * This is also why the earned banner going from a solid brand fill to chrome did not require
 * re-tuning these tones — spanning the ramp is what makes the burst independent of whatever
 * the banner happens to be doing behind it.
 *
 * Named `brand`, never "purple" — the product white-labels this colour.
 *
 * REDUCED MOTION RENDERS NOTHING AT ALL. Not a static scatter of pieces, not a faded version:
 * confetti IS the motion, and a frozen burst is visual noise over a banner whose own tick and
 * "EARNED" already carry the outcome. That is why this returns `null` rather than pinning the
 * animation to `none` the way the accordions above do — for them the end state is the content,
 * here the end state is nothing.
 */
const RewardConfetti = () => {
  const theme = useTheme()
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)')

  if (reduceMotion) return null

  const tones = [
    theme.palette.common.white,
    theme.palette.brand[200],
    theme.palette.brand[300],
    theme.palette.brand[600],
    theme.palette.brand[800],
  ]

  return (
    <Box
      /*
        `aria-hidden` and `pointer-events: none`: decorative, and the tick + "EARNED" beside
        it already announce the outcome to a screen reader. Announcing fourteen empty divs as
        well would be noise on top of a message that has already landed.

        `position: absolute; inset: 0` inside the banner's own relative box, so it contributes
        NOTHING to layout — the banner does not grow, reflow, or shift when this mounts and
        unmounts. Pieces that fly past the banner are clipped by the block's existing
        `overflow: hidden`, which is why the travel distances above stay modest.
      */
      aria-hidden
      sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1 }}
    >
      {CONFETTI_PIECES.map((p, i) => (
        <Box
          key={i}
          style={
            {
              '--dx': `${p.dx}px`,
              '--peak': `${p.peak}px`,
              '--dy': `${p.dy}px`,
              '--rot': `${p.rot}deg`,
            } as CSSProperties
          }
          sx={{
            position: 'absolute',
            left: `${p.left}%`,
            top: '50%',
            width: p.round ? 5 : 4,
            height: p.round ? 5 : 8,
            borderRadius: p.round ? '50%' : '1px',
            backgroundColor: tones[p.tone],
            opacity: 0,
            animation: `${rewardBurst} ${p.duration}ms cubic-bezier(0.15, 0.6, 0.35, 1) ${p.delay}ms forwards`,
          }}
        />
      ))}
    </Box>
  )
}

const NUMBER_WORD = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine']
const spell = (n: number): string => NUMBER_WORD[n] ?? String(n)

/**
 * One staged block — its steps and the activation they pay out.
 *
 * PROGRESSIVE DISCLOSURE, because fifteen rows is a card tall enough to push Recent activity
 * below the fold. Exactly one block is open at a time: by default the first unfinished one,
 * which is the only one the user can act on. The others collapse to a summary line that still
 * carries their count, so the whole journey stays legible without being rendered.
 *
 * A finished block keeps its line rather than disappearing — "5/5, activation earned" is the
 * receipt for a credit the brand now holds, and deleting it would erase the only place on
 * Home that says where those activations came from.
 */
const ChecklistBlockSection = ({
  block,
  index,
  isOpen,
  onToggle,
  onClaimed,
}: {
  block: ChecklistBlock
  index: number
  isOpen: boolean
  onToggle: () => void
  /**
   * Fired once a claim actually credits. The parent PINS its accordion to this block in
   * response — see `handleToggle`'s neighbourhood for why the open block cannot be left
   * derived through a claim.
   */
  onClaimed: () => void
}) => {
  const theme = useTheme()
  const { open: openSnackbar } = useSnackbar()
  const { mutate: claimReward, isPending: isClaiming } = useGrantSetupCompletionReward()

  /*
    THE BURST IS TIED TO THE CLICK'S OUTCOME, NOT TO `block.earned`.

    `earned` is true on every subsequent page load for the rest of the company's life, so
    celebrating on it would either fire the confetti on every visit or need a persisted
    "already celebrated" flag to suppress itself — which is the shape of state the old
    auto-claim had, and the reason it failed. A click is unambiguous and happens exactly once,
    so the moment is already identified and needs nothing stored.

    Gated further on `grantedBlocks` actually containing THIS block: a repeat claim is a
    server-side no-op returning `granted: false`, and throwing confetti for a credit that was
    already held would be celebrating nothing.
  */
  const [isCelebrating, setIsCelebrating] = useState(false)
  const celebrateTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(
    () => () => {
      if (celebrateTimer.current) clearTimeout(celebrateTimer.current)
    },
    [],
  )

  const remaining = block.total - block.completedCount
  const stepWord = remaining === 1 ? 'step' : 'steps'

  const handleClaim = () => {
    claimReward(block.id, {
      onSuccess: (result) => {
        if (!result.grantedBlocks.includes(block.id)) return
        /*
          PIN THE ACCORDION OPEN BEFORE ANYTHING ELSE. The parent's open-block is DERIVED
          while untouched, and this block stops matching that derivation the instant the
          credit lands (it becomes complete-and-earned, so the derivation moves on). Left
          alone, the block would therefore collapse out from under the user in the same
          render that pays them — hiding the tick, the EARNED badge and the confetti that
          are the entire response to the thing they just did.

          Pinning also converts the accordion from derived to explicit for the rest of the
          session, which is the intended side effect: after acting, the user drives it.
        */
        onClaimed()
        setIsCelebrating(true)
        if (celebrateTimer.current) clearTimeout(celebrateTimer.current)
        celebrateTimer.current = setTimeout(() => setIsCelebrating(false), REWARD_CONFETTI_MS)
      },
      /*
        A failed claim MUST say so. The whole defect being fixed here is a credit that never
        arrived while the UI said it would, so a click that spins and then silently returns to
        an unclaimed button would be the same failure with a shorter timeline. This is the
        toast policy's own case for a toast: the outcome of the action is invisible on screen.
      */
      onError: () =>
        openSnackbar({
          type: 'error',
          title: 'Could not add your activation',
          text: 'Nothing was charged or changed. Try again in a moment.',
        }),
    })
  }

  return (
    <Stack
      /*
        ⚠️ NO `spacing` HERE, DELIBERATELY — the gap lives INSIDE the collapsible below.

        This Stack has exactly two children: the header row and the collapsing region. A
        `spacing={3}` between them looked right and was wrong when collapsed, because a
        flex GAP is not part of either child's height. `grid-template-rows: 0fr` takes the
        collapsing child to 0, but the 12px gap survives it and lands entirely BELOW the
        header — measured on a collapsed block: 13px padding-top, 24px header, 12px orphan
        gap, 0px content, 13px padding-bottom. The label itself sat 16px from the top and
        28px from the bottom, which is the "top and bottom padding should match" report.
        The padding was symmetric the whole time; the gap was not.

        Moving that 12px to the collapsing wrapper's own `pt` makes it part of what animates
        to zero, so an expanded block keeps exactly today's rhythm and a collapsed one has
        none. Preferred over zeroing the gap conditionally on `isOpen`: a gap that snaps
        12→0 while the height eases over 240ms reads as a jolt at the moment the eye is on
        it, and this way there is nothing to keep in sync with `collapsibleSx`'s timing.
      */
      sx={{
        p: 3,
        borderRadius: 2,
        /*
          NEUTRAL border and NO fill, open or closed. Brand purple on this card is reserved
          for ONE thing — the reward — so that the eye lands on the offer rather than on
          whichever section happens to be expanded. A tint behind four rows plus a purple
          outline made the open block the loudest element on the page, louder than the thing
          it was meant to be selling. Expansion itself says which block is open.
        */
        border: `1px solid ${theme.palette.primary[100]}`,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={3}
        onClick={onToggle}
        sx={{ cursor: 'pointer' }}
      >
        <Stack direction="row" alignItems="center" spacing={3} sx={{ minWidth: 0 }}>
          {/* The block's own tick — earned as a unit, so it reads at the block level too.
              24px, the SAME as a row's tick and the reward's gift: these three are the
              card's left-hand marker column, so any difference between them reads as
              misalignment rather than hierarchy. */}
          <Box
            sx={{
              width: MARKER,
              height: MARKER,
              flexShrink: 0,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              bgcolor: block.isComplete ? DONE_MARKER_FILL(theme) : 'transparent',
              border: block.isComplete ? 'none' : `1px solid ${INCOMPLETE_MARKER_BORDER(theme)}`,
              /**
               * Conditional, because the two states sit on DIFFERENT backgrounds.
               * `white[100]` is correct for the completed tick (white on a brand[500]
               * fill) and was being applied to the incomplete state too — where the
               * background is transparent, i.e. the card. A near-white numeral on a card
               * is the "too hard to read" the user reported.
               */
              color: block.isComplete ? DONE_MARKER_TICK(theme) : theme.palette.text.secondary,
              fontSize: 12,
              fontWeight: block.isComplete ? 400 : 600,
              lineHeight: 1,
            }}
          >
            {block.isComplete ? <CheckIcon size={14} strokeWidth={2} /> : index + 1}
          </Box>
          <Typography variant="body3" noWrap sx={{ fontWeight: block.isComplete ? 400 : 500 }}>
            {block.title}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={3} sx={{ flexShrink: 0 }}>
          <Typography variant="body3" color="text.secondary">
            {block.completedCount}/{block.total}
          </Typography>
          {/* ONE icon, rotated — not a swap between an up glyph and a down glyph. A swap is an
              instant cut with nothing to animate; rotating the same chevron 180deg is the thing
              that actually moves. */}
          <Box sx={{ display: 'grid', placeItems: 'center', ...chevronRotateSx(isOpen) }}>
            <ChevronDownIcon size={16} strokeWidth={1} />
          </Box>
        </Stack>
      </Stack>

      {/* See `collapsibleSx`'s docblock: a `grid-template-rows` transition, not a conditional
          render — this is what makes the block open/close ANIMATE rather than snap. The content
          stays mounted at all times so it can be measured/transitioned to and from 0. */}
      <Box sx={collapsibleSx(isOpen)}>
        {/*
          TWO elements, and the split is the fix — not one element with padding on it.

          The header→content gap that used to be the parent Stack's `spacing` now lives on
          the INNER Stack's `pt`. It cannot live on the grid item (the outer Box): a grid
          item's own padding is part of its border box, so `grid-template-rows: 0fr` zeroes
          the CONTENT height and leaves the 12px padding standing — measured, the collapsed
          block stayed at 62px with the padding moved but still on the item. `overflow:
          hidden` does not help there either, because an element never clips its own padding.

          Put one level down it becomes ordinary content of a zero-height, overflow-hidden
          parent, and is clipped along with the rows. The outer Box is the clipper and holds
          no spacing of its own; the inner Stack holds all of it.
        */}
        <Box sx={{ overflow: 'hidden', minHeight: 0 }}>
          <Stack spacing={3} sx={{ pt: 3 }}>
          <Stack>
            {block.items.map((item) => (
              <ChecklistRow key={item.id} item={item} />
            ))}
          </Stack>

          {/*
            THE REWARD, per block rather than once at the end — a line, not a sixth row. Every
            row above is something you DO; this is something you GET, so as a row it would make
            the counter read "5 of 6" while claiming a task no user action can tick.

            Shown from the start, not revealed on completion: a reward first displayed at the
            moment it is earned lands after the conversion it was meant to drive, which makes
            it a thank-you rather than an incentive.
          */}
          {/*
            EARNED IS THE CHECKMARKS' PURPLE; UNEARNED IS THE CHROME.

            ⚠️ THIS REVERSES THE PREVIOUS ARRANGEMENT, ON AN EXPLICIT USER DIRECTIVE
            (2026-09-05): "the white chrome we currently have if you haven't earned it yet
            with the claim button, and then when it has been earned it turns into purple,
            that would match the checkmarks on the homepage style."

            The version this replaces had it the other way round — brand tint on the OFFER,
            neutral chrome on the RECEIPT — argued from the principle that a finished thing
            is the lowest-information element on screen and should therefore go quiet. That
            argument is preserved verbatim in `DONE_MARKER_FILL`'s docblock and is NOT being
            discarded here. What changes is which VOCABULARY expresses "quiet".

            The reconciliation, and the reason this is not simply the loud fill coming back a
            fifth time: the earned banner now wears exactly the treatment a COMPLETED STEP
            already wears — `DONE_MARKER_FILL` over `DONE_MARKER_TICK`, the same two values,
            not a new saturated fill. That treatment exists *because* it is the muted one; its
            own docblock is the argument for muting it. So the receipt is still quiet, it is
            just quiet in the same language as every other completed thing on this card,
            rather than in a neutral language of its own.

            MEASURED, not asserted (2026-09-05, dark mode, 1280x800):
              the completed-step disc  rgba(143,104,234,0.16) over rgb(15,15,18) → rgb(35,29,53)
              this banner, earned      rgba(143,104,234,0.16) over rgb(15,15,18) → rgb(35,29,53)
            Identical, because the two sit on the SAME opaque surface (the Card). That is the
            whole reason "match the checkmarks" is expressible as a shared token here and
            would NOT be if the banner sat on a filled parent — an alpha is a relationship to
            whatever is behind it, and this file has shipped three defects from forgetting
            that. Re-measure rather than re-deriving if the block ever gains a background.

            The unearned banner keeps the neutral chrome and, more to the point, keeps the
            CLAIM BUTTON — a solid primary control, which is what actually carries the offer's
            attention now that the fill does not. The band no longer has to shout, because the
            thing inside it does.

            `block.earned` is the LEDGER, not `block.isComplete`. Ticking four rows and being
            credited are two facts — the grant is a separate write that can lag or fail — and
            a card claiming a credit the account does not hold is the worst version of this.
          */}
          <Stack
            direction="row"
            spacing={3}
            alignItems="center"
            sx={{
              // `relative` so the confetti overlay can pin itself to this banner without
              // taking part in layout. Nothing else depends on it.
              position: 'relative',
              p: 3,
              borderRadius: 2,
              /**
               * TWO TREATMENTS — the RECEIPT is brand, the OFFER is chrome.
               *
               *   earned → THE COMPLETED-STEP TREATMENT, REUSED. `DONE_MARKER_FILL` for the
               *     fill and `DONE_MARKER_TICK` for the glyph — the SAME two functions the
               *     row ticks and the block tick call, not a re-typed colour. That is the
               *     point: "matches the checkmarks" has to be true by construction, or it
               *     becomes true today and false the first time someone retunes the ticks.
               *
               *     Both resolve to palette tokens, never a literal hex — `brand[500]`
               *     (#8F68EA) at 0.16 and `brand[600]` (#7F48DF). Named `brand`, never
               *     "purple": the product white-labels its brand colour, so naming a token
               *     by its hue freezes an assumption the product is built to break. The
               *     user's word for this was "purple"; the code's word is `brand`.
               *
               *     The border is `brand['600-32%']`, the house brand hairline already used
               *     elsewhere on this card. The disc has no border because a 24px circle
               *     does not need one; a 644px band does, or its ends dissolve.
               *
               *   unearned → CHROME. A 4% neutral wash and a neutral hairline: on the dark
               *     card a white/silver outline, on the light card a graphite one. It is the
               *     quieter of the two states now, which is correct — the band no longer has
               *     to carry the offer on its own, because the Claim button sitting in it is
               *     a solid primary control and is the loudest thing in the row either way.
               *
               *     PER-MODE ALPHA on the border, because one number cannot hold the 3:1 bar
               *     on both cards. `alpha()` REPLACES the alpha channel rather than
               *     multiplying it, and the two modes' `text.primary` are a near-white
               *     literal and MUI's default `rgba(0,0,0,0.87)`, so the same alpha lands on
               *     very different greys:
               *
               *       dark   rgba(250,250,250,0.36) over #0f0f12 → rgb(100,100,102) → 3.24:1
               *              ↳ MEASURED in the browser. 0.32 was 2.78:1, under the bar.
               *       light  rgba(0,0,0,0.44)       over #ffffff → rgb(143,143,143) → 3.23:1
               *              ↳ DERIVED from the theme's own literals (`background.paper` is
               *                `#ffffff`, light `text.primary` is MUI's default), not
               *                measured: the app's colour mode lives in `localStorage` and is
               *                read once at mount, so flipping it to check would have altered
               *                a live session. At 0.36 light would have been 2.52:1 — the
               *                reason this is split rather than left as one value. Re-measure
               *                if you are ever in light mode with this on screen.
               *
               * ALPHA OF `text.primary`, NEVER A LITERAL WHITE, for the chrome state.
               * `text.primary` is the page's own foreground and is therefore correct in both
               * modes by construction — near-white on the dark card (the "white chrome" look
               * this is asked to have) and near-black on the light one. A literal
               * `common.white` would have been invisible on a light card, which is the exact
               * class of bug this file already records three of in a single day
               * (`primary[950]` remapping to #ffffff in dark, `common.white` on a light
               * panel, `white[100]` over a state with no fill). Alpha also composites
               * against whatever surface is behind it rather than assuming one.
               */
              /*
                ⚠️ THE LIGHT ALPHA DROPPED 0.44 → 0.13. Spencer, 2026-09-22: "the border on
                the claim activation card is too dark — should be light grey."

                The 3:1 argument written above DOES NOT APPLY TO THIS BORDER, and that is
                the substantive reason this is allowed to move rather than a preference
                overriding an accessibility finding.

                WCAG 1.4.11 governs "visual information required to identify user interface
                components and states". This hairline identifies nothing: the banner is
                identified by its fill, its gift icon, its sentence and the button inside it,
                every one of which survives the border being faint. Compare the `earned`
                border two lines up, which DOES answer to 3:1 — it is one of the two things
                distinguishing earned from unearned, so it is carrying state. Same file,
                same property, different obligation, because 1.4.11 is about what the pixel
                COMMUNICATES, not about what kind of pixel it is.

                So the per-mode split below is no longer holding a contrast floor — it is
                just two greys. Light goes to a genuine light grey; DARK IS UNCHANGED at
                0.36, because the complaint was about the light card and a 0.13 white
                hairline on #0f0f12 would effectively erase the band's edge.
              */
              border: `1px solid ${
                block.earned
                  ? theme.palette.brand['600-32%']
                  : alpha(theme.palette.text.primary, theme.palette.mode === 'light' ? 0.13 : 0.36)
              }`,
              bgcolor: block.earned
                ? DONE_MARKER_FILL(theme)
                : alpha(theme.palette.text.primary, 0.04),
              color: 'text.primary',
              /*
                The arrival beat, kept — but it is now the ONLY thing marking the transition
                besides the confetti, where before the banner also changed colour entirely.
                A 420ms rise is small enough to stay quiet on the re-loads where it fires
                without a claim behind it. Honours reduced-motion.
              */
              ...(block.earned && {
                animation: `${rewardLand} 420ms cubic-bezier(0.32, 0.72, 0, 1)`,
                '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
              }),
            }}
          >
            {isCelebrating && <RewardConfetti />}
            <Box sx={{ width: MARKER, height: MARKER, flexShrink: 0, display: 'grid', placeItems: 'center' }}>
              {/* `DONE_MARKER_TICK` once earned — the SAME value the completed ticks use, so
                  the gift reads as one more finished mark rather than as a second dialect
                  for the same idea. `brand[500]` while it is still an offer.

                  MEASURED: brand[600] #7F48DF on the earned band's composited rgb(35,29,53)
                  is 3.00:1 — it clears the 3:1 bar for a non-text graphic, which is what an
                  icon is, and it is the ratio the shipping checkmark ticks already run at
                  (same ink, same tint, same backdrop). It does NOT clear 4.5:1, which is why
                  the "EARNED" text below stays `text.primary` rather than following the
                  glyph to brand. */}
              {/* THREE states, not two. Brand ink is the app's "there is something to
                  act on" colour, so spending it on a reward the user cannot yet claim
                  devalues it everywhere else — the gift sat purple from the first
                  unticked row, competing with the actual next action.

                  not complete  -> text.primary : an aspiration. Present, legible, inert.
                  isComplete    -> brand[500]   : CLAIMABLE. The copy says "ready" and
                                                  there is a button; the glyph should
                                                  pull toward it.
                  earned        -> DONE_MARKER_TICK : banked, same ink as the finished
                                                  ticks, per the note above.

                  `text.primary` rather than a literal white so light mode gets near-black
                  instead of an invisible glyph. Contrast is not a concern in this
                  direction — text.primary is the highest-contrast ink on the surface. */}
              <GiftIcon
                size={18}
                strokeWidth={1}
                color={
                  // CLAIMABLE is the only state that gets brand ink. Earned and
                  // not-yet-earned are both `text.primary`.
                  //
                  // Earned was DONE_MARKER_TICK, to rhyme with the completed row ticks.
                  // Against the white banner copy it read as muddy rather than
                  // finished — and the state is already stated twice beside it (the
                  // word "granted" and the EARNED chip), so the glyph does not need to
                  // carry it a third time. Brand ink stays reserved for the one state
                  // with something to act on.
                  block.isComplete && !block.earned
                    ? theme.palette.brand[500]
                    : theme.palette.text.primary
                }
              />
            </Box>
            <Typography variant="body3" sx={{ lineHeight: 1.5, flex: 1 }}>
              {/* Counted from the block, never spelled out. This read "Finish these five"
                  after the blocks went from five items to four — a hardcoded number in copy
                  beside the number it describes, which is the one place it is guaranteed to
                  be caught and the one place it looks worst. */}
              {block.earned
                ? /*
                     `earned` is a LEDGER fact and `isComplete` is LIVE state, and they can
                     disagree: the credit is granted when every item is done and is never
                     clawed back, but an item can later regress (a predicate that was true
                     stops being true). Observed 2026-09-12 — the `setup` block showed
                     "granted" beside an unticked "Style your certificates page".
                     
                     Saying only "granted" there reads as the card contradicting itself,
                     since an unticked row sits directly above it. Naming both facts is
                     honest and, unlike the bare sentence, tells the user the step is worth
                     redoing. The credit is NOT at risk and the copy must not imply it is.
                   */
                  block.isComplete
                  ? '1 free certification activation granted.'
                  : '1 free certification activation granted. A step above has come undone — your activation is safe.'
                : block.isComplete
                ? /*
                     WAS "Activation granted shortly…", and that sentence was a lie the product
                     could not keep: the grant it referred to fired from a hook that could only
                     ever see the completing TRANSITION, so a user who completed a step and then
                     reloaded sat on this line permanently with an empty ledger. Nothing was
                     coming, ever.

                     Replaced with a statement of fact plus a button that makes it true. "Ready"
                     rather than "granted": the credit is not on the account until the click
                     lands, and this card's own rule is that it never claims a credit the
                     account does not hold.
                   */
                  'Your free certification activation is ready.'
                : block.isEarnedOverTime
                  ? `Finish these ${spell(block.total)} and earn a free certification activation. These arrive as your customers use your certificates.`
                  : `Finish these ${spell(block.total)} and earn a free certification activation.`}
            </Typography>
            {/*
              EARNED, right-aligned, with a tick — the banked state said so only in prose
              ("1 free certification activation granted"), which reads the same shape as
              every other sentence on the card. A word in caps at the far edge is scannable
              without reading, which is the whole complaint this change answers.

              Gated on `block.earned` (the LEDGER) for the same reason the fill is, per the
              note above: `isComplete` means the rows are ticked, `earned` means the credit
              actually landed. Claiming a credit the account does not hold is the failure
              worth avoiding, and a tick + "EARNED" claims it far more loudly than prose did.

              `flexShrink: 0` because the sibling copy is `flex: 1` and the over-time
              variant is long — without it this collapses at 1280 where the sidebar is
              expanded. `aria-hidden` on the tick: "EARNED" already says it, so announcing
              a checkmark as well would double it for a screen reader.
            */}
            {block.earned ? (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                {/* `text.primary`, not `common.white` and NOT the brand ink the gift beside
                    it now uses — see the glyph note above. MEASURED on the earned band's
                    composited rgb(35,29,53): `text.primary` is 15.49:1, brand[600] is
                    3.00:1. A 3:1 graphic is fine; 3:1 small TEXT is not, and "EARNED" is
                    text. So the two marks in this row are deliberately different colours,
                    and that is the reason. On the dark card this IS white; on a light card a
                    literal white would have put the word on a near-invisible band. */}
                <CheckIcon size={16} strokeWidth={2} color={theme.palette.text.primary} aria-hidden />
                <Typography
                  variant="body3"
                  sx={{ fontWeight: 600, letterSpacing: '0.06em', color: 'text.primary' }}
                >
                  EARNED
                </Typography>
              </Stack>
            ) : (
              /*
                THE CLAIM. This replaces an auto-fire that structurally could not run for most
                users (see `useChecklistProgressToast`'s note) — the credit now depends on an
                action that cannot be missed rather than on an observer that happened to be
                mounted at the right instant.

                RENDERED EVEN WHILE THE BLOCK IS INCOMPLETE, disabled with a reason, rather
                than appearing on completion. §46's rule is permission → disable + tooltip,
                record state → hide; this is neither, it is the OFFER the banner exists to
                make. Hiding it until the work is done would repeat the mistake the banner's
                own docblock argues against for the banner itself: a reward first shown at the
                moment it is earned lands after the conversion it was meant to drive. Showing
                the button greyed with "2 steps left" is the incentive doing its job.

                `disabledReason` (not a wrapping Tooltip) because a natively disabled button
                fires no pointer events at all, so its explanation is unreachable for exactly
                the people it is written for. The shared Button's prop handles that — it
                withholds the native attribute, keeps the control focus-reachable, and
                announces `aria-disabled`.

                Default size, i.e. 40px (§60) — never `size="small"`.

                "CLAIM activation", not "Get earned activation". `claim` is the accurate verb:
                eligibility is derived SERVER-side from the twelve predicates, so the credit is
                already earned before this button exists — pressing it banks something owed, it
                does not cause the earning. `Get earned…` implied the button was what made it
                earned, and carried the word "earned" twice over (the banner beside it, and the
                EARNED badge that replaces it a second later).

                No separate in-flight label: the shared Button's `loading` puts a spinner
                BESIDE its children, so the label stays put rather than swapping to
                "Claiming…" and reflowing the banner mid-request.
              */
              <Box sx={{ flexShrink: 0 }}>
                <Button
                  variant="primary"
                  /*
                    NO `surface="brand-tint"` ANY MORE — deliberately removed, not
                    forgotten.

                    That prop existed because the UNEARNED band used to be a
                    brand-tinted callout, and the shared Button's disabled primary
                    is a neutral grey chip sized for a white/near-black card: on a
                    brand tint that grey read as a dead foreign element sitting on
                    top of the offer. The band is now the neutral chrome wash
                    (`alpha(text.primary, 0.04)`, measured at rgb(24,24,27) — 1.08:1
                    against the card, i.e. all but the card itself), so the DEFAULT
                    surface is the correct one again and `brand-tint` would now be
                    the mismatch it was added to prevent.

                    Kept as a note because the prop is otherwise unused in the repo
                    — this was its only call site — and a future reader finding it
                    orphaned should know it was a considered removal tied to this
                    band's fill, not dead code to delete from the Button.
                  */
                  disabled={!block.isComplete}
                  loading={isClaiming}
                  disabledReason={
                    block.isEarnedOverTime
                      ? // "Finish" is the wrong verb for block 3 — a scan, a lead and a transfer
                        // are things that HAPPEN to a brand, not things it can go and do. Naming
                        // what is outstanding honestly matters more than one reusable string.
                        `${remaining} ${stepWord} to go. These complete as your customers use your certificates.`
                      : `Finish the remaining ${remaining} ${stepWord} to earn this.`
                  }
                  onClick={handleClaim}
                >
                  Claim activation
                </Button>
              </Box>
            )}
          </Stack>
          </Stack>
        </Box>
      </Box>
    </Stack>
  )
}

/**
 * The company setup checklist (VRG-340) — fifteen steps in three staged blocks.
 *
 * COMPANY-scoped, and rendered only for `manage_company` holders — the caller decides that.
 * A Design hire joining a set-up brand does not see this block at all; their half of Home is
 * the getting-started tours, which is USER-scoped. The two are deliberately not one list.
 */
export const SetupChecklist = ({
  // Defaulted, not merely typed as required. A missing `blocks` used to throw on
  // `blocks.find` — and because a crash on Home files an auto error report and redirects,
  // the redirect landed back on Home and the whole thing looped. A prop this component
  // cannot render without is worth one cheap default rather than a crash cycle.
  blocks = [],
  moreItems = [],
  completedCount,
  total,
  isCollapsed,
  onToggleCollapsed,
  isToggling,
  isSingleSeat = false,
}: {
  blocks: ChecklistBlock[]
  /**
   * "More" — worth doing, NEVER gated on a reward and never counted.
   *
   * Inviting a teammate and referring a brand are real things a brand should do, but neither
   * moves it toward a working certificate, and gating an activation on them punishes the case
   * that is most common at signup: one person, no one to invite yet. Keeping them visible but
   * outside the count is what lets the fifteen gated steps stay finishable by a solo founder.
   */
  moreItems: ChecklistItem[]
  completedCount: number
  total: number
  /** Collapsed to just this card's header. Reversible — see `useCollapseChecklist`. */
  isCollapsed: boolean
  onToggleCollapsed: () => void
  isToggling?: boolean
  /**
   * A single-seat company (Starter, Individual — `seats.limit === 1`, VRG-227). On a plan
   * that IS one person, "your company" reads oddly — there is no company to organise, just
   * this one account — so the card's title swaps to "your account" instead. Defaulted false
   * (the safer, more-common multi-seat reading) rather than required, matching how the rest
   * of this component tolerates a missing prop.
   */
  isSingleSeat?: boolean
}) => {
  /*
    Which block is open. Defaults to the first block the user can still ACT on — but the user
    can open any of them, including finished ones to re-read what they did.

    "Can still act on" is NOT `!isComplete`. A block whose rows are all ticked but whose
    activation has not been claimed still holds a button, and collapsing it hides that button
    behind a chevron the user has no reason to press — the block looks finished, its summary
    line says 4/4, and the one thing left to do in it is out of sight. That is how the reward
    goes unclaimed a second way, having just been fixed the first way.

    So a block is only settled once it is complete AND credited. Once claimed, `earned` flips
    and normal behaviour resumes — this block stops being the default and the next one opens.
  */
  const firstIncomplete =
    blocks.find((b) => !b.isComplete || !b.earned)?.id ?? blocks[blocks.length - 1]?.id
  /*
    THREE states, not two, and the third is the point: `undefined` means the user has not
    touched the accordion, so it falls back to the first unfinished block — the only one they
    can act on. `null` means they explicitly closed one, and closing must be allowed to leave
    EVERYTHING closed.

    With a plain `string | null`, closing the open block set state to null, which read as
    "untouched" and immediately re-opened the default — so the section could never actually be
    collapsed, only swapped.
  */
  const [openBlockId, setOpenBlockId] = useState<string | null | undefined>(undefined)
  const openId = openBlockId === undefined ? firstIncomplete : openBlockId

  /*
    CLOSING A BLOCK OPENS THE NEXT ONE — the accordion always has somewhere to be, so the card
    never collapses into three identical summary lines with nothing to read. Closing block 1
    advances to block 2, which is also where the user's attention would go next anyway.

    ⚠️ EXCEPT AT THE END OF THE LIST, where closing the LAST block closes everything.

    Considered and rejected: wrapping back to block 1. Two reasons, and the second is the
    decisive one.

      1. A downward-pointing "close" chevron that scrolls the user back to the TOP of the card
         is a control doing something other than what it depicts. Closing should never feel
         like navigation.
      2. It would make "everything closed" UNREACHABLE. The three-state `undefined | null |
         string` above exists specifically so an explicit close can leave everything shut —
         that is the bug its own docblock records (`null` reading as "untouched" and instantly
         re-opening the default, so the section could only ever be swapped, never collapsed).
         Wrapping would reintroduce exactly that, by a different route.

    So: advance while there is somewhere to advance to, and let the last one be the terminus.
    Opening a DIFFERENT block than the open one is unchanged — it just opens.

    On block-level permission: there is deliberately no filtering here, because a block does
    not carry a permission. Gating is PER ROW (`ChecklistRow` disables a row the viewer cannot
    action and names the missing permission), and the card as a whole is already gated on
    `manage_company` by `HomePage`. Skipping blocks on a permission that is not modelled at
    this level would be inventing a rule, not respecting one.
  */
  const handleToggle = (blockId: string) => {
    if (openId !== blockId) {
      setOpenBlockId(blockId)
      return
    }
    const index = blocks.findIndex((b) => b.id === blockId)
    // `?? null` is the end-of-list terminus: no next block means everything closes.
    setOpenBlockId(blocks[index + 1]?.id ?? null)
  }

  return (
    // No `spacing` on the Card — see the note on the collapsible below. Card's spacing is a
    // margin on its second child, and a margin cannot be collapsed by `grid-template-rows: 0fr`.
    <Card>
      <Stack direction="row" alignItems="baseline" justifyContent="space-between">
        {/* "your company", not "your brand" and not "Get started". Company is the word the
            product uses everywhere else — the companies table, the company_admin role, the
            manage_company permission, 23 routes, the Company card below — and "brand" was a
            third dialect for the same noun. "Get started" was the other candidate and loses
            because it collides with the "Getting started" tours block, which still renders
            for anyone without a checklist.

            EXCEPT on a single-seat plan (VRG-227): there is no "company" to organise beyond
            this one person, so "Set up your company" reads like it is addressing a team that
            does not exist. "Your account" is the same swap `SettingsPage` already makes for
            its own single-seat collapse — same predicate, same word. */}
        <Typography variant="h6">
          {isSingleSeat ? 'Set up your account' : 'Set up your company'}
        </Typography>
        {/* Progress, not a done/not-done state: partial onboarding is the common case, and
            "2 of 6" tells you there is a path where a bare unchecked box does not.

            The SAME pill as the sidebar Home row, deliberately — a reader meeting "1/6" in the
            nav and "1 of 6" here should recognise one fact, not two. `/` rather than " of " so
            the two are literally identical. */}
        {/*
          Dismiss sits in the CARD, next to the count, not in the page header.
          It only ever affected this block — Home itself is permanent — so a control in the
          page header implied it dismissed the PAGE, and it kept rendering after the block was
          gone, which is a button whose only effect had already happened. Beside the count it
          is unambiguous: this card, and the number it is showing you.

          `text` variant so it stays the quietest thing in the row. Skipping setup is a
          legitimate choice, not one to push.
        */}
        <Stack direction="row" spacing={3} alignItems="center">
          <ProgressPill>
            {completedCount}/{total}
          </ProgressPill>
          {/*
            COLLAPSE, not dismiss. The escape hatch is real — a solo operator will never
            "invite a teammate", so on a permanent Home an unfinishable checklist would nag
            forever — but it does not have to be destructive to provide it. Collapsing keeps
            the header and the count, so the answer to "how far am I" survives, and expanding
            is one click.

            The chevron alone, with a tooltip: at this size a word ("Collapse"/"Expand") next
            to a count reads as another action competing with the rows, and the direction of
            the caret already says which way it goes.
          */}
          <Tooltip title={isCollapsed ? 'Show setup steps' : 'Collapse'} placement="top">
            <IconButton size="small" onClick={onToggleCollapsed} disabled={isToggling}>
              {/* ONE icon, rotated — see the block-level chevron above for why a swap
                  doesn't animate and a rotation does. */}
              <Box sx={{ display: 'grid', placeItems: 'center', ...chevronRotateSx(!isCollapsed) }}>
                <ChevronDownIcon size={18} strokeWidth={1} />
              </Box>
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/*
        The SHARED `Progress`, not a hand-rolled LinearProgress. Its brand gradient is the
        app's existing progress language — Billing's usage meter and the table cells already
        speak it — and this bar was a one-off flat fill that quietly said the same thing in a
        different dialect. It also brings the mode-aware track for free; the hand-rolled
        version used `brand[100]`, a light-mode tint that painted a near-white bar across the
        card in dark mode.

        Honest about the payoff: at 1 of 6 the fill is ~17% of the track, so only the
        gradient's light end is visible and it reads much like a flat fill. The gain is
        consistency and the shared dark-mode handling, not visible richness at low progress.
      */}
      {/* Always mounted (like the block-level accordion above) so `collapsibleSx` has content
          to transition to/from 0 — see its docblock for why this is a grid-rows transition
          rather than the conditional render this replaced. */}
      {/*
        THE SAME ORPHAN-SPACE FIX AS THE BLOCK ACCORDION, one level up — found by measuring
        this card rather than reported, but it is the identical defect and would have been
        the next thing noticed.

        `Card`'s `spacing={4}` put a 16px MARGIN-TOP on this Box (MUI's Stack spacing is a
        margin on the sibling, not a `gap` — measured: `margin-top: 16px`). A margin sits
        OUTSIDE the border box, so `grid-template-rows: 0fr` cannot touch it: a fully
        collapsed card still carried 16px of empty space under its header.

        So the card takes no `spacing` and the 16px moves onto the inner Stack's `pt`, inside
        the clipping Box, exactly as the block does. Note the Box is now purely the clipper —
        it must hold no padding or margin of its own, because neither would collapse.
      */}
      <Box sx={collapsibleSx(!isCollapsed)}>
        <Box sx={{ overflow: 'hidden', minHeight: 0 }}>
          <Stack spacing={4} sx={{ pt: 4 }}>
          {/* `value` is a PERCENTAGE, not a count — that is `Progress`'s actual contract, and it
              is easy to get wrong: `min`/`max` look like they scale the bar but only feed the end
              LABELS, and the default branch hands the raw `value` straight to MUI. Passing the raw
              count 1 with max 6 therefore rendered a 1% fill, not a sixth. Billing passes a
              pre-computed percentage for the same reason.

              `bottomLabels={['','']}` suppresses those end labels, which otherwise printed "0" and
              "100" under the track, restating the pill two inches above. */}
          <Progress
            value={total > 0 ? (completedCount / total) * 100 : 0}
            min={0}
            max={100}
            bottomLabels={['', '']}
          />

          <Stack spacing={3}>
            {blocks.map((block, i) => (
              <ChecklistBlockSection
                key={block.id}
                block={block}
                index={i}
                isOpen={openId === block.id}
                onToggle={() => handleToggle(block.id)}
                onClaimed={() => setOpenBlockId(block.id)}
              />
            ))}
          </Stack>

          {/*
            MORE — deliberately outside the numbered blocks, with no tick count and no reward.

            These are worth doing and worth surfacing, but they are not on the path to a working
            certificate, so gating an activation on them would punish the most common signup:
            one person with nobody to invite. Rendered flat rather than as a fourth block so the
            absence of a number reads as "optional", not as "a block you have not started".
          */}
          {moreItems.length > 0 && (
            <Stack spacing={2}>
              <Typography
                variant="body3"
                color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: 11 }}
              >
                More
              </Typography>
              <Stack>
                {moreItems.map((item) => (
                  <ChecklistRow key={item.id} item={item} />
                ))}
              </Stack>
            </Stack>
          )}

          {/*
            THE REWARD CAPSTONE — a line, not a seventh row, and the distinction is the whole
            design. Every row above is something you DO; this is something you GET. As a row it
            would make the counter read "6 of 7" while claiming a task that no user action can ever
            tick, and the denominator is a promise about how much work is left.

            Rendered from the START, not revealed at completion. Completing the checklist means
            ordering tags and adding a payment method, so by the time someone finishes they have
            already paid and committed — a reward first shown at that moment lands after the
            conversion it was meant to drive, which makes it a thank-you rather than an incentive.
            Visible from row one, it is working the whole time.
          */}
          {/*
            The reward is the only thing on this card the user GETS rather than does, so it keeps
            its own surface instead of a divider and a grey line.

            DIMMED from the first version, which used a full `brand[500]` border over
            `navSelected` and read as the brightest thing on the page — louder than the checklist
            it is meant to reward. It only has to stand out from the ROWS above it, not compete
            with them: `600-32%` for the border and the subtler nav tint for the fill keep it
            distinctly an offer while sitting a step back. Still short of a solid fill, which is
            reserved for primary actions — this is an incentive, and nothing in it is clickable.
          */}
          </Stack>
        </Box>
      </Box>
    </Card>
  )
}
